/**
 * Pure, SHARED in-world clock derivation + formatting (Time & Weather System
 * R2). Lives in `shared-types` so BOTH the lambda chronology engine AND the web
 * UI derive the date/day-phase from the SAME negative-safe code — there is no
 * second copy of the Euclidean-modulo discipline to drift (a naive `%` would
 * corrupt a time-travel / pre-epoch clock). Type-only dependencies; no I/O.
 *
 * The engine side (`@rufus/lambda/src/chronology`) re-exports these and adds the
 * op-producing helpers (advanceClock/setClock) + the time-advance registry.
 */
/** Euclidean floor-division: always rounds toward negative infinity. */
export function floorDiv(n, d) {
    return Math.floor(n / d);
}
/** Euclidean modulo: result always has the divisor's sign (here non-negative) —
 *  `floorMod(-1, 60) === 59`. The one place a naive `%` would corrupt a
 *  pre-epoch (negative) clock, centralized. */
export function floorMod(n, d) {
    return ((n % d) + d) % d;
}
/**
 * A SIGNED decimal with an explicit leading sign (`+5`, `-5`, `+0`) — the one
 * place the `+`/`-` is chosen (two-model gate Medium #18). Interpolating
 * `` `+${n}` `` renders a negative value as `+-5`; this picks the sign from the
 * value so a past-ward (negative) clock delta / year reads correctly. */
export function signed(n) {
    return n < 0 ? String(n) : `+${n}`;
}
/** Break a clock into {year, month, day, hour, minute} against its calendar (R2). */
export function breakdown(clock) {
    const { minutesPerHour, hoursPerDay, daysPerMonth, monthsPerYear } = clock.calendar;
    const minutesPerDay = minutesPerHour * hoursPerDay;
    const daysPerYear = daysPerMonth * monthsPerYear;
    const m = clock.minuteOfEpoch;
    const minute = floorMod(m, minutesPerHour);
    const hour = floorMod(floorDiv(m, minutesPerHour), hoursPerDay);
    const dayOfEpoch = floorDiv(m, minutesPerDay);
    const day = floorMod(dayOfEpoch, daysPerMonth);
    const month = floorMod(floorDiv(dayOfEpoch, daysPerMonth), monthsPerYear);
    const year = floorDiv(dayOfEpoch, daysPerYear);
    return { year, month, day, hour, minute, dayOfEpoch };
}
function pad2(n) {
    return String(n).padStart(2, "0");
}
/** Format a clock as a human/DM/UI string (R2), e.g. `"Day 412, 14:30"`. Negative-safe. */
export function formatClock(clock, opts = {}) {
    const b = breakdown(clock);
    const hm = `${pad2(b.hour)}:${pad2(b.minute)}`;
    const label = clock.calendar.epochYearLabel;
    if (opts.withYearLabel && label) {
        // `signed(b.year)` so a pre-epoch year reads `-1`, not `+-1` (Medium #18).
        return `Year ${label}${b.year !== 0 ? ` ${signed(b.year)}` : ""}, Month ${b.month + 1} Day ${b.day + 1}, ${hm}`;
    }
    return `Day ${b.dayOfEpoch + 1}, ${hm}`;
}
/** Sensible day-phase boundaries when a calendar sets none (dawn 06:00, day 08:00,
 *  dusk 18:00, night 20:00 as fractions of a 24h day). */
export const DEFAULT_DAY_PHASE_BOUNDARIES = {
    dawn: 0.25,
    day: 0.3333,
    dusk: 0.75,
    night: 0.8333,
};
/**
 * The day-phase for a clock (R2) — derived from the time-of-day fraction.
 * Negative-safe. `calendar` overrides the clock's own calendar for the WHOLE
 * derivation (two-model gate Medium #19): the override is threaded into
 * {@link breakdown} too, so the hour and the minutes-per-day come from the SAME
 * calendar rather than silently blending the clock's calendar (via breakdown)
 * with the override (via minuteOfDay). Omit it to use the clock's own calendar.
 */
export function dayPhase(clock, calendar = clock.calendar) {
    // Derive against the chosen calendar consistently: if an override is given,
    // break the clock down with THAT calendar, not the clock's embedded one.
    const effective = calendar === clock.calendar ? clock : { ...clock, calendar };
    const b = breakdown(effective);
    const minuteOfDay = b.hour * calendar.minutesPerHour + b.minute;
    const minutesPerDay = calendar.minutesPerHour * calendar.hoursPerDay;
    const fraction = minuteOfDay / minutesPerDay;
    const bounds = calendar.dayPhaseBoundaries ?? DEFAULT_DAY_PHASE_BOUNDARIES;
    if (fraction >= bounds.night || fraction < bounds.dawn)
        return "night";
    if (fraction >= bounds.dusk)
        return "dusk";
    if (fraction >= bounds.day)
        return "day";
    return "dawn";
}
//# sourceMappingURL=clock-format.js.map